import { AbsoluteCenter } from '@chakra-ui/react';
import { ReactNode, useCallback } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import classNames from 'classnames/bind';
import { Box, Flex } from 'shared';
import { VARIANTS } from 'shared_DEPRECATED/components/Button';
import { FlexItem } from 'shared_DEPRECATED/components/Flex';
import { FormErrorMessage } from 'shared_DEPRECATED/components/Form/ErrorMessage';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import Spacer from 'shared_DEPRECATED/components/Spacer';

import { ChatDragAndDropPopover } from 'features/chat/components/ChatDragAndDropPopover';
import {
  MAX_MESSAGE_LENGTH_ERROR_TEXT,
  MAX_MESSAGE_LENGTH,
  CHAT_FORM_FIELD_NAMES,
  ATTACHMENT_TYPES,
} from 'features/chat/config';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';
import { RenderIfCoach } from 'features/user/components/RenderIfCoach';

import {
  ModalBody,
  ModalHeader,
  useModalContext,
  ModalFooter,
} from 'shared/components/Modal';
import { ModalButton } from 'shared/components/Modal/ModalButton';
import { Typography } from 'shared/components/Typography';

import { ChatMessageScheduleButton } from '../ChatMessagesScheduling';
import styles from 'features/chat/components/Chat.module.css';

const cx = classNames.bind(styles);

type ChatFileModalProps = {
  files: File[];
  children: ReactNode;
  scheduleSubmitHandler: (text: string, files: UnuploadedAttachment[]) => void;
  type: typeof ATTACHMENT_TYPES[keyof typeof ATTACHMENT_TYPES];
  onSubmit: (text: string, files: UnuploadedAttachment[]) => void;
};

export const ChatFileModal = ({
  files: newFiles,
  children,
  onSubmit,
  type,
  scheduleSubmitHandler,
}: ChatFileModalProps) => {
  const { chatFormMethods }: { chatFormMethods: ReturnType<typeof useForm> } =
    useModalContext();

  const messageText = useWatch({
    control: chatFormMethods.control,
    name: CHAT_FORM_FIELD_NAMES.TEXT,
  });

  const methods = useForm({
    defaultValues: {
      [CHAT_FORM_FIELD_NAMES.TEXT]: messageText,
      [CHAT_FORM_FIELD_NAMES.FILES]: newFiles.map((file) => ({
        name: file.name,
        contentLength: file.size,
        contentType: file.type,
        url: URL.createObjectURL(file),
        file,
      })),
    },
  });

  const handleFormSubmit = methods.handleSubmit(
    ({ [CHAT_FORM_FIELD_NAMES.TEXT]: text }) => {
      if (files.length > 0) {
        onSubmit(text, files);
      }
    }
  );

  const files = useWatch({
    control: methods.control,
    name: CHAT_FORM_FIELD_NAMES.FILES,
  }) as UnuploadedAttachment[];

  const handleScheduleButtonClick = useCallback(() => {
    scheduleSubmitHandler(messageText, files);
  }, [messageText, files, scheduleSubmitHandler]);

  return (
    <FormProvider {...methods}>
      <ChatDragAndDropPopover
        onDrop={(file) =>
          methods.setValue(CHAT_FORM_FIELD_NAMES.FILES, [
            ...files,
            {
              name: file.name,
              contentLength: file.size,
              contentType: file.type,
              url: URL.createObjectURL(file),
              file,
            },
          ])
        }
      >
        <ModalHeader>
          <Typography type="smallHeading" fontWeight="semibold" color="black">
            {files.length > 0
              ? `Send ${files.length} ${type}(s)`
              : `Select ${type}(s)`}
          </Typography>
        </ModalHeader>
        <ModalBody styles={{ maxHeight: '60vh' }}>{children}</ModalBody>
        <ModalFooter>
          <Flex flexDirection="column" width="100%">
            <Flex justifyContent="space-around">
              <Flex alignItems="center" position="relative" width="100%">
                <TextareaAutosize
                  placeholder="Add caption"
                  className={cx('chat__input', 'chat__input--gray')}
                  maxRows={3}
                  data-testid="add-caption-textarea"
                  {...methods.register(CHAT_FORM_FIELD_NAMES.TEXT, {
                    maxLength: {
                      value: MAX_MESSAGE_LENGTH,
                      message: MAX_MESSAGE_LENGTH_ERROR_TEXT,
                    },
                    setValueAs: (v) => v.trim(),
                  })}
                />
                <RenderIfCoach>
                  <AbsoluteCenter axis="vertical" right="0">
                    <ChatMessageScheduleButton
                      onClick={handleScheduleButtonClick}
                    />
                  </AbsoluteCenter>
                </RenderIfCoach>
              </Flex>
              <Spacer size="zr sm zr md" />
              <FlexItem flexShrink={0}>
                <Box style={{ borderRadius: '12rem', overflow: 'hidden' }}>
                  <ModalButton
                    onClick={handleFormSubmit}
                    color={VARIANTS.PRIMARY}
                    shouldClose={methods.formState.isValid}
                    dataTestid="send-file-modal-bnt"
                    p={0}
                    style={{
                      borderRadius: '50%',
                      overflow: 'hidden',
                    }}
                  >
                    <Icon
                      name={iconNames.paperPlane}
                      height="18px"
                      width="18px"
                      stroke="var(--fgPrimaryInverse)"
                    />
                  </ModalButton>
                </Box>
              </FlexItem>
            </Flex>
            {methods.formState.errors.text?.message && (
              <Spacer size="lg zr zr">
                <FormErrorMessage
                  fieldName="text"
                  dataTestid="chat-file-modal-caption-field-validation"
                />
              </Spacer>
            )}
          </Flex>
        </ModalFooter>
      </ChatDragAndDropPopover>
    </FormProvider>
  );
};
